import { Configuration, BrowserCacheLocation, InteractionType, InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService  } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { filter, from, switchMap, take } from 'rxjs';

// this checks if the app is running on IE
export const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/** =================== REGIONS ====================
 * 1) B2C policies and user flows
 * 2) Web API configuration parameters
 * 3) Authentication configuration parameters
 * 4) MSAL-Angular specific configuration parameters
 * =================================================
*/

// 1) B2C policies and user flows
export const b2cPolicies = {
    names: {
        signUpSignIn: environment.b2cSignUpSignIn,
        resetPassword: environment.b2cResetPassword
    },
    authorities: {
        signUpSignIn: {
            authority: `https://${environment.b2cLogin}/${environment.b2cDomain}/${environment.b2cSignUpSignIn}`
        },
        resetPassword: {
            authority: `https://${environment.b2cLogin}/${environment.b2cDomain}/${environment.b2cResetPassword}`
        }
    }
}

// 2) Web API configuration parameters
export const apiConfig: {b2cScopes: string[], webApi: string} = {
    b2cScopes: [`https://${environment.b2cDomain}/${environment.b2cApiClientId}/api.consume`],
    webApi: environment.apiBaseUrl
};

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.b2cClientId,
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    redirectUri: environment.b2cRedirectUri,
    postLogoutRedirectUri: environment.b2cPostLogoutRedirectUri,
    knownAuthorities: [environment.b2cLogin]
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage
  }
};

// 3) Authentication configuration parameters
export const loginRequest: {scopes: string[]} = {
    scopes: ['openid', 'profile'],
};

export const tokenRequest: {scopes: string[]} = {
    scopes: apiConfig.b2cScopes
};

// 4) MSAL-Angular specific configuration parameters
export const msalGuardConfig: MsalGuardConfiguration = {
  interactionType: InteractionType.Redirect,
  authRequest: {
    scopes: [...loginRequest.scopes, ...tokenRequest.scopes],
  }
};

export const msalInterceptorConfig: MsalInterceptorConfiguration = {
  interactionType: InteractionType.Redirect,
  protectedResourceMap: new Map([
    [apiConfig.webApi, apiConfig.b2cScopes],
  ])
};

export const msalInterceptor: HttpInterceptorFn = (req, next) => {
  const msal = inject(MsalService);
  const msalBroadcast = inject(MsalBroadcastService);

  if (req.url.startsWith(apiConfig.webApi)) {
    // Escuta o status de interação e trava a requisição até o MSAL terminar o redirecionamento
    return msalBroadcast.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        // Agora é seguro pegar a conta
        const account = msal.instance.getActiveAccount() || msal.instance.getAllAccounts()[0];
        if (account) {
          return from(msal.acquireTokenSilent({
            scopes: apiConfig.b2cScopes,
            account: account
          })).pipe(
            switchMap(result => {
              if (result.accessToken) {
                // console.log(result.accessToken);
                const authReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${result.accessToken}` }
                });
                return next(authReq);
              }
              return next(req);
            })
          );
        }
        return next(req);
      })
    );
  }
  return next(req);
};